import { AUTHORS } from './authors';
import { PageMeta } from 'constants/pages';
import { BOOK_A_DEMO_LINK } from 'constants/common';
import { EHashtag, TCardProps } from 'types/common';
import { ArticleSections } from 'types/articles';

// TODO: Replace placeholder images with actual .webp images in assets/resources/scenario-matching-drone-intelligence/
import HeaderImage from 'assets/resources/scenario-matching-drone-intelligence/header-section.webp';
import ScenarioMatchingImage from 'assets/resources/scenario-matching-drone-intelligence/scenario-matching.webp';

const commonData = {
  ...AUTHORS.AILiveSim,
  title:
    'Battle-Ready by Design: How Scenario Matching Turns Battlefield Knowledge into Reusable Drone Intelligence',
  publishDate: 'March 25, 2026',
  minutesOfRead: 8,
};

export const eighthApril2026ArticleMeta: PageMeta = {
  url: '/articles/scenario-matching-drone-intelligence',
  title: commonData.title,
  description:
    'Discover how scenario matching turns battlefield knowledge into reusable drone intelligence, enabling scalable testing and faster model improvement for defense programs.',
};

export const eighthApril2026ArticleData: ArticleSections[] = [
  {
    resourceType: 'Article',
    image: HeaderImage,
    imageCaption: 'Battle-Ready by Design with AILiveSim',
    type: 'header',
    linkedInPostId: 'https://www.linkedin.com/company/ailivesim/',
    text: (
      <>
        <p>
          <em>AILiveSim Defense Drone Series | Article 1 of 3</em>
        </p>
      </>
    ),
    ...commonData,
  },

    {
    type: 'common',
    title: 'The Battlefield Moves Faster Than Static Test Plans',
    text: (
      <>
        <p>
          Drone software built on fixed test suites has a shelf life measured in weeks. Threat
          behaviors shift. Terrain changes hands. Visibility windows collapse without warning.
          Tactical conditions that mattered last month may be irrelevant by the next deployment cycle.
        </p>
        <p>
          The question facing defense programs is not whether a drone was tested. It is whether it was
          tested against the situations it will actually face. Most programs answer that question with
          some version of &ldquo;we tested what we could think of.&rdquo; Scenario matching exists to
          close that gap.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Drone Readiness Is a Scenario Coverage Problem',
    text: (
      <>
        <p>
          Drone training is often framed as a data challenge or a compute challenge. In practice, the
          harder problem is scenario discovery and coverage. Teams need a structured way to capture
          what experienced operators already know and then scale that knowledge across hundreds of test
          conditions.
        </p>
        <p>
          Without that structure, engineering teams either overtrain on narrow, clean scenarios or
          spend months manually authoring edge cases that still miss the unexpected.
        </p>
        <p>
          The bottleneck is getting the right situations into the training pipeline. Processing power
          has never been the constraint that stalls programs.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'What Scenario Matching Actually Does',
    text: (
      <>
        <p>
          Subject matter experts define a meaningful base scenario: an anti-drone evasion maneuver, an
          urban low-visibility intercept, a terrain-masking approach, a detection failure observed
          during a previous deployment. AILiveSim encodes that knowledge into structured scenario
          templates.
        </p>
        <p>
          The platform then matches new live or historical data back to relevant base scenarios. Once
          matched, the system generates hundreds of controlled variations from that starting point.
        </p>
        <p>
          Variations adjust weather, time of day, speed, distance, obstacle layout, approach angle,
          and atmospheric visibility.
        </p>
        <p>
          Each variation is automatically labeled and ready for training. The institutional knowledge
          embedded in the original scenario carries forward into every variation the platform
          generates.
        </p>
      </>
    ),
        image: ScenarioMatchingImage,
    imageCaption: 'Scenario matching workflow',
  },

  {
    type: 'common',
    title: 'Closing the Loop Between Field Operations and Model Improvement',
    text: (
      <>
        <p>
          Mission recordings and sensor feeds reveal gaps, failures, and conditions that were
          underrepresented in training data. AILiveSim uses those signals to identify the closest base
          scenarios and generate targeted synthetic expansions.
        </p>
        <p>
          A concrete example: a drone struggles during low-visibility urban operations against
          fast-moving aerial threats. The platform matches that incident to a base evasion scenario.
          Hundreds of variants are generated across fog density levels, alley geometry, approach
          vectors, and sensor noise profiles. The model is retrained and validated against that
          expanded dataset before the next deployment. That turns operational feedback into fast model
          improvement instead of post-mission reports sitting in a queue for months.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Where AILiveSim Fits in the Development Cycle',
    text: (
      <>
        <p>
          AILiveSim preserves institutional knowledge in reusable form and connects scenario creation,
          matching, variation, training, and validation in one loop through the Automated Training
          Pipeline (ATP). ATP analyzes datasets, identifies underrepresented conditions, and selects
          base scenarios to generate coverage-optimized synthetic data automatically. Teams do not
          rebuild every scenario from scratch.
        </p>
        <p>
          When testing is the priority rather than training, the Intelligent System Testing (IST)
          module takes over. IST acts as an automated red team, searching for failure modes across the
          full parameter space. For defense applications, auditable test coverage is a deployment
          prerequisite. It cannot be treated as an afterthought. See our technical brief on IST{' '}
          <a
            href="https://www.ailivesim.com/technical-briefs/classical-simulation-testing-doesnt-scale"
            target="_blank"
            rel="noopener noreferrer"
          >
            here
          </a>
          .
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'The Operational Advantage',
    text: (
      <>
        <p>
          Battlefield adaptation depends on how fast knowledge becomes training coverage. When a pack
          of drones, say 100 or more, in the field encounter a new threat type, the pipeline reruns,
          retrains, and revalidates. Scenario matching connects expert intuition and field data to
          scalable software improvement. For defense programs where software obsolescence means mission
          failure, that speed is what keeps fielded systems relevant.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Ready to Take the Next Step?',
    text: (
      <>
        <p>
          <strong>
            <a href={BOOK_A_DEMO_LINK} target="_blank" rel="noopener noreferrer">
              Book Your Demo Today
            </a>
          </strong>
        </p>
        <p>
          <strong>Resources:</strong>
        </p>
        <ul>
          <li>
            <a
              href="https://www.ailivesim.com/articles/thermal-camera-sensors-ailivesim-platform"
              target="_blank"
              rel="noopener noreferrer"
            >
              Thermal Camera Sensors &amp; AILiveSim
            </a>
          </li>
          <li>
            Read our White Paper about the sim to real gap:{' '}
            <a
              href="https://ailivesim.com/whitepapers/using-synthetic-data-from-ailivesim"
              target="_blank"
              rel="noopener noreferrer"
            >
              Using Synthetic Data from AILiveSim to Train Robust Maritime Detection Models
            </a>
          </li>
          <li>
            <a href="/articles/ailivesim-glossary">AILiveSim Glossary of Terms</a>
          </li>
        </ul>
      </>
    ),
  },
];

export const eighthApril2026ArticleCard: TCardProps = {
  description:
    'Discover how scenario matching turns battlefield knowledge into reusable drone intelligence, enabling scalable testing and faster model improvement for defense programs.',
  image: HeaderImage,
  type: 'Article',
  category: 'articles',
  postUrl: eighthApril2026ArticleMeta.url,
  ...commonData,
  hashTags: [
    EHashtag.SyntheticData,
    EHashtag.DigitalTwin,
    EHashtag.DefenseTechnology,
    EHashtag.Robotics,
    EHashtag.ObjectDetection,
  ],
};
